import * as React from "react"
import {
  Box,
  Button,
  Heading,
  Text,
  Image,
  Grid,
  GridItem,
  SimpleGrid,
  Card,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SkeletonText,
} from "@chakra-ui/react"
import { Player } from '../Player'
import { useDisclosure } from "@chakra-ui/react"
import { contractConfig } from '../config'
import { ethers } from 'ethers'
import { abi } from '../contract-abi'
import { useTranslation } from 'react-i18next'
import { dailyFeature, metadata } from '../metadata'
import { BASE64_IMAGES } from "../constants/images"
import { PowerRewardModal } from './PowerRewardModal'

interface GameListProps {
  address?: string;
  chainId: string;
  isConnected: boolean;
  todaysGameSeed?: string | null;
  renderConnectButton: () => React.ReactNode;
  renderEmptyStateButton?: () => React.ReactNode;
  onGamePlay: (nft: NFT) => void;
}

interface NFT {
  name: string;
  id?: string;
  tokenId: string;
  productCode?: string;
  isFreePlay?: boolean;
}


export const GameList: React.FC<GameListProps> = ({ 
  address, 
  chainId, 
  isConnected, 
  todaysGameSeed, 
  renderConnectButton, 
  renderEmptyStateButton,
  onGamePlay 
}) => {
  const { t } = useTranslation();
  const [ownedNfts, setOwnedNfts] = React.useState<null | any[]>(null);
  const [selected, setSelected] = React.useState<string | false>(false);
  const [title, setTitle] = React.useState('Game');
  const [network, setNetwork] = React.useState(chainId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = React.useState(false);
  const previousAddress = React.useRef(address);
  const [todaysGames, setTodaysGames] = React.useState<NFT[]>([]);
  const [powerReward] = React.useState<{ amount: number; reason: 'connect' | 'play' | 'share' } | null>(null);
  const { isOpen: isPowerRewardOpen, onClose: onPowerRewardClose } = useDisclosure();

  const getTodaysGames = React.useCallback((todaysGameSeed: string) => {
    if (!todaysGameSeed) return [];
    const today = new Date().toISOString().split('T')[0];
    const seed = todaysGameSeed + today;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash = hash & hash;
    }
  
    const validProductCodes = dailyFeature;
    const validMetadataEntries = Object.entries(metadata).filter(([_, { productCode }]) => validProductCodes.includes(productCode));
  
    if (validMetadataEntries.length < 2) {
      console.error("Not enough valid games found for today's selection.");
      return [];
    }
  
    const firstIndex = Math.abs(hash) % validMetadataEntries.length;
    let secondIndex = Math.abs(hash * 31) % validMetadataEntries.length;
  
    if (secondIndex === firstIndex) {
      secondIndex = (secondIndex + 1) % validMetadataEntries.length;
    }
  
    const firstGame = {
      name: validMetadataEntries[firstIndex][1].name,
      productCode: validMetadataEntries[firstIndex][1].productCode,
      isFreePlay: true
    };
  
    const secondGame = {
      name: validMetadataEntries[secondIndex][1].name,
      productCode: validMetadataEntries[secondIndex][1].productCode,
      isFreePlay: true
    };
  
    return [firstGame, secondGame];
  }, []);

  const handleBackButton = React.useCallback((event?: PopStateEvent) => {
    if (event) event.preventDefault();
    if(isOpen) {
      onClose();
      setSelected(false);
      window.history.pushState(null, '', window.location.pathname);
    }
  }, [isOpen, onClose]);

  const fetchNFTs = React.useCallback(async () => {
    try {
      let enrichedNfts: NFT[] = [];

      if (address) {
        console.log('fetching nfts for ', network);
        const provider = new ethers.JsonRpcProvider(
          contractConfig[network as keyof typeof contractConfig].rpcUrl
        );
        const contract = new ethers.Contract(
          contractConfig[network as keyof typeof contractConfig].address,
          abi,
          provider
        );
        const tokenIds = await contract.owned(address);

        tokenIds.forEach((tokenId: string) => {
          let realId = String(BigInt(tokenId) - (1n << 255n));
          const nft = {
            id: realId,
            tokenId: tokenId,
            productCode: metadata[realId as keyof typeof metadata].productCode,
            name: metadata[realId as keyof typeof metadata].name,
            isFreePlay: false,
          };
          enrichedNfts.push(nft as NFT);
        });
      }

      setOwnedNfts(enrichedNfts);
      setLoading(false);

      // Check URL hash on mount
      const hash = window.location.hash.slice(1);
      if (hash && enrichedNfts.length > 0) {
        const nft = enrichedNfts.find((n: NFT) => n.productCode === hash);
        if (nft) {
          setSelected(nft.productCode || false);
          setTitle(nft.name);
          onOpen();
        }
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [address, network, onOpen]);

  React.useEffect(() => {
    if (todaysGameSeed) {
      const todaysGamesList = getTodaysGames(todaysGameSeed);
      console.log('loaded todays games: ', todaysGamesList);
      setTodaysGames(todaysGamesList as NFT[]);
    } else {
      setTodaysGames([]);
    }
  }, [todaysGameSeed, getTodaysGames]);
  React.useEffect(() => {
    window.addEventListener('popstate', handleBackButton);

    const loadData = async () => {
      if (chainId) {
        if (previousAddress.current !== address) {
          setOwnedNfts(null);
          previousAddress.current = address;
        }
        if (network !== chainId) {
          setOwnedNfts(null);
          setNetwork(chainId);
        }
        if (!loading && ownedNfts === null) {
          setLoading(true);
          try {
            await fetchNFTs();
          } catch (error) {
            console.error("Error loading metadata:", error);
            setLoading(false);
          }
        }
      } else {
        setLoading(false);
        setOwnedNfts(null);
      }
    };

    if (address && isConnected) {
      loadData();
    } else {
      setOwnedNfts(null);
    }

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [address, chainId, isConnected, handleBackButton, loading, ownedNfts, network, fetchNFTs]);

  const handleGamePlay = (nft: NFT) => {
    onGamePlay(nft);
    setSelected(nft.productCode || false);
    setTitle(nft.name);
    window.history.pushState(null, '', '#'+nft.productCode);
    onOpen();
  };

  return (
    <>
      <Player 
        selected={selected} 
        setSelected={setSelected}
        title={title}
        onOpen={onOpen} 
        isOpen={isOpen}
        onClose={handleBackButton} 
      />
      
      <Box textAlign="center" fontSize="md">
        <Grid minH="40vh" p={3}>
          <Grid gap={4}>
            <GridItem colSpan={5} h='10'>
              <HStack align="start" mt={2}>
                <Menu>
                  <MenuButton as={Button} padding={0}>
                    <Image
                      display="inline"
                      align="left"
                      maxHeight="33px"
                      src={process.env.PUBLIC_URL + "/icon_gameboy.svg"}>
                    </Image>
                  </MenuButton>
                  <MenuList>
                    <MenuItem as="a" href="/">
                      <Image
                        display="inline"
                        align="left"
                        maxHeight="20px"
                        src={process.env.PUBLIC_URL + "/icon_gameboy.svg"}>
                       </Image>
                      &nbsp;Game Boy
                    </MenuItem>
                    <MenuItem color="gray.500">
                      To be continued...
                    </MenuItem>
                  </MenuList>
                </Menu>
      
                <Image
                  src={process.env.PUBLIC_URL + "/retro_logo.svg"}
                  display={isConnected ? "inline" : { base: "none", sm: "inline" }}
                  maxWidth={isConnected ? { base: "160px", sm: "180px" } : "180px"}
                  paddingTop={isConnected ? { base: "4px", sm: 0 } : 0}
                  maxHeight="40px"
                  align="left"
                  className="logo"
                />
              </HStack>
            </GridItem>
            <GridItem colStart={6} colEnd={6} h='10'>
              <Box display="flex" justifyContent="flex-end" paddingTop="6px">
                {renderConnectButton()}
              </Box>
            </GridItem>
          </Grid>

          {/* Loading State */}
          {loading && (
            <>
              <Box className="notice">&nbsp;</Box>
              <SimpleGrid className="game-list" columns={[2, 2, 3, 4]} maxWidth={1200} margin="auto" spacing={[0, 0, 4, 8]} marginTop={20}>
                {Array(4).fill(null).map((_, index) => (
                  <Box key={index} className="card" marginBottom={[10, 10, 6, 2]}>
                    <Image src={BASE64_IMAGES.cartridge_loading} />
                    <Card boxSize="90%" marginLeft="5%" mt={4} height="70px">
                      <SkeletonText className="code" noOfLines={2} pr="8px" />
                    </Card>
                  </Box>
                ))}
              </SimpleGrid>
            </>
          )}

          {/* Empty State */}
          {!loading && (!ownedNfts || ownedNfts.length === 0) && todaysGames.length === 0 && (
            <SimpleGrid className="empty-state" columns={1} maxWidth={800} margin="auto" marginTop={20} marginBottom={20}>
              <Image src={process.env.PUBLIC_URL + "/new.svg"} width={150} ml="auto" mr="auto"></Image>
              <Heading fontSize="3xl" fontWeight="normal" fontFamily="silkscreen" lineHeight={2}>
                {t('welcome.title')}
              </Heading>
              <Text fontSize="md" color="gray.500">
                {t('welcome.description')}
              </Text>
              {renderEmptyStateButton ? renderEmptyStateButton() : renderConnectButton()}
            </SimpleGrid>
          )}

          {/* Game List */}
          {!loading && (todaysGames.length > 0 || (ownedNfts && ownedNfts.length > 0)) && (
            <>
              <Box className="notice">
                {t('gameList.tapToPlay')}
              </Box>
              <SimpleGrid className="game-list" columns={[2, 2, 3, 4]} maxWidth={1200} margin="auto" spacing={[0, 0, 4, 8]} marginTop={20}>
                {todaysGames.map((nft, index) => (
                  <Box key={`today-${index}`} className="card" marginBottom={[10, 10, 6, 2]} position="relative">
                    <Box position="relative"
                      onClick={() => handleGamePlay(nft)}
                      _hover={{
                        cursor: "pointer"
                      }}
                    >
                      <Box
                        position="absolute"
                        top="4%"
                        left="8%"
                        width="84%"
                        height="20%"
                        zIndex={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Image
                          src={process.env.PUBLIC_URL + "/ribbon.svg"}
                          alt="ribbon"
                          width="100%"
                          height="100%"
                        />
                        <Text
                          position="absolute"
                          textAlign="center"
                          width="100%"
                          top="26%"
                          fontSize={["2.4vw", "1.8vw", "1.2vw", "0.8em"]}
                          fontWeight="bold"
                          color="white"
                          zIndex={1}
                          lineHeight="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {t('gameList.todaysGame')}
                        </Text>
                      </Box>
                      <Image
                        src={'/images/'+nft.productCode+'.webp'}
                        fallbackSrc={BASE64_IMAGES.cartridge_loading}
                        alt={nft.name}
                        className="cartridge"
                      />
                    </Box>
                    <Card boxSize="90%" marginLeft="5%" mt={4} height="70px">
                      <Text className="code"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            color="#A83866"
                            pr="8px">{nft.productCode}</Text>
                      <Text className="title"
                            display="-webkit-box"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            sx={{
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                            pr="8px">{nft.name}</Text>
                    </Card>
                  </Box>
                ))}
                {ownedNfts && ownedNfts.map((nft, index) => (
                  <Box key={index} className="card" marginBottom={[10, 10, 6, 2]}>
                    <Image
                      src={'/images/'+nft.productCode+'.webp'}
                      fallbackSrc={BASE64_IMAGES.cartridge_loading}
                      alt={nft.name}
                      className="cartridge"
                      onClick={() => handleGamePlay(nft)}
                      _hover={{
                        cursor: "pointer",
                      }} />
                    <Card boxSize="90%" marginLeft="5%" mt={4} height="70px">
                      <Text className="code"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            color="#A83866"
                            pr="8px">{nft.productCode}</Text>
                      <Text className="title"
                            display="-webkit-box"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            sx={{
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                            pr="8px">{nft.name}</Text>
                    </Card>
                  </Box>
                ))}
              </SimpleGrid>
            </>
          )}
        </Grid>
      </Box>
      <PowerRewardModal 
        isOpen={isPowerRewardOpen}
        onClose={onPowerRewardClose}
        amount={powerReward?.amount || 0}
        reason={powerReward?.reason || 'play'}
      />
    </>
  );
};

export default GameList; 